
import React, { useState, useEffect } from "react";
import "./App.css";
import AddressAutocomplete from "./AddressAutocomplete";
import MapAddressSelector from "./MapAddressSelector";
import ResultsMapView from "./ResultsMapView";
import InstallPrompt from "./InstallPrompt";
import { useMobileOptimization } from "./hooks/useMobileOptimization";
import { findPlacesAtMidpoint, photoUrlFromReference } from "./FrontSearchService";

/** ---------- Helpers (shareable URL) ---------- **/
const serializeStateToQuery = ({ searchType, filters, locations }) => {
  try {
    const p = new URLSearchParams();
    if (searchType) p.set("type", searchType);
    if (filters?.radius) p.set("r", String(filters.radius));
    if (filters?.restaurant_type) p.set("c", filters.restaurant_type);
    if (filters?.price_level != null) p.set("p", String(filters.price_level));
    if (filters?.min_rating != null) p.set("m", String(filters.min_rating));
    if (filters?.open_now) p.set("o", "1");
    (locations || []).slice(0, 2).forEach((loc, i) => {
      if (typeof loc.lat === "number" && typeof loc.lng === "number") {
        p.set("l" + (i + 1), `${loc.lat.toFixed(6)},${loc.lng.toFixed(6)}`);
      }
    });
    return p.toString();
  } catch {
    return "";
  }
};

const applyQueryToState = () => {
  const q = new URLSearchParams(window.location.search);
  const next = {};
  const type = q.get("type");
  if (type) next.searchType = type;
  const r = Number(q.get("r"));
  if (r) next.radius = r;
  const c = q.get("c");
  if (c) next.cuisine = c;
  const p = q.get("p");
  if (p != null) next.price = Number(p);
  const m = q.get("m");
  if (m != null) next.min = Number(m);
  const o = q.get("o") === "1";
  const parseLL = (s) => {
    if (!s) return null;
    const [a, b] = s.split(",").map(Number);
    return isFinite(a) && isFinite(b) ? { lat: a, lng: b } : null;
  };
  const L = [];
  const v1 = parseLL(q.get("l1"));
  const v2 = parseLL(q.get("l2"));
  if (v1) L.push({ address: "", ...v1 });
  if (v2) L.push({ address: "", ...v2 });
  return { next, L, open: o };
};

/** ---------- Tiny Components ---------- **/
function CardSkeleton() {
  return (
    <div className="animate-pulse border border-gray-200 rounded-lg overflow-hidden">
      <div className="h-48 bg-gray-200" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-gray-200 w-2/3" />
        <div className="h-3 bg-gray-200 w-1/2" />
        <div className="h-3 bg-gray-200 w-1/3" />
      </div>
    </div>
  );
}

function Chips({ autoSearch, setAutoSearch, filters, setFilters, setSortOrder, onSearch }) {
  const Chip = ({ active, onClick, children }) => (
    <button
      onClick={() => { onClick(); if (autoSearch) onSearch(); }}
      className={`px-3 py-1.5 rounded-full text-sm border transition ${active ? "bg-gray-900 text-white border-gray-900" : "bg-white hover:bg-gray-50"}`}
    >
      {children}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      <Chip
        active={!!filters.min_rating && Number(filters.min_rating) >= 4}
        onClick={() => setFilters((f) => ({ ...f, min_rating: f.min_rating ? null : 4 }))}
      >
        ⭐ 4+
      </Chip>
      <Chip
        active={filters.price_level === 2}
        onClick={() => setFilters((f) => ({ ...f, price_level: f.price_level === 2 ? null : 2 }))}
      >
        €€
      </Chip>
      <Chip
        active={!!filters.open_now}
        onClick={() => setFilters((f) => ({ ...f, open_now: !f.open_now }))}
      >
        Ouverts
      </Chip>
      <Chip
        active={false}
        onClick={() => setSortOrder("distance_asc")}
      >
        Distance ↑
      </Chip>
    </div>
  );
}

function FixedActionBar({ searchRestaurants, autoSearch, setAutoSearch, filters, setFilters }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 border-t bg-white/95 backdrop-blur shadow-md md:hidden">
      <div className="max-w-screen-sm mx-auto p-3 flex items-center gap-2">
        <button
          onClick={searchRestaurants}
          className="flex-1 px-4 py-3 rounded-xl bg-indigo-600 text-white font-semibold"
        >
          Rechercher
        </button>
        <label className="flex items-center gap-2 px-3 py-2 rounded-xl border">
          <input
            type="checkbox"
            checked={!!autoSearch}
            onChange={(e) => {
              const enabled = e.target.checked;
              setAutoSearch(enabled);
              if (enabled) searchRestaurants();
            }}
          />
          Auto
        </label>
        <label className="flex items-center gap-2 px-3 py-2 rounded-xl border">
          <input
            type="checkbox"
            checked={!!filters.open_now}
            onChange={(e) => {
              setFilters({ ...filters, open_now: e.target.checked });
            }}
          />
          Ouverts
        </label>
      </div>
    </div>
  );
}

/** ---------- Component ---------- **/
function App() {
  const [locations, setLocations] = useState([{ address: "", lat: null, lng: null }]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [searchType, setSearchType] = useState("restaurant");
  const [filters, setFilters] = useState({
    radius: 5000,
    restaurant_type: "",
    price_level: null,
    min_rating: null,
    open_now: false,
  });
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [mapSelectorIndex, setMapSelectorIndex] = useState(0);

  const [resultViewMode, setResultViewMode] = useState("list");
  const [autoSearch, setAutoSearch] = useState(false);
  const [autoHint, setAutoHint] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [prefetchCache, setPrefetchCache] = useState({});
  const [showTour, setShowTour] = useState(false);
  const [sortOrder, setSortOrder] = useState("rating_desc");
  const { isMobile, vibrate } = useMobileOptimization();
  const [isDark, setIsDark] = useState(false);

  const track = (event, payload = {}) => {
    try {
      if (window.dataLayer) window.dataLayer.push({ event, ...payload });
    } catch {}
  };

  /** ---------- Init ---------- **/
  useEffect(() => {
    try {
      if (!localStorage.getItem("miparcours_tour_done")) setShowTour(true);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      setIsDark(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
    } catch {}
  }, []);

  // Default open now (lunch/dinner hours)
  useEffect(() => {
    const h = new Date().getHours();
    const shouldOpen = (h >= 11 && h <= 14) || (h >= 18 && h <= 22);
    setFilters((f) => ({ ...f, open_now: shouldOpen }));
    if (shouldOpen) setAutoHint("Filtre “Ouverts maintenant” activé automatiquement (11–14h / 18–22h).");
  }, []);

  // Apply URL params (type, filters, first 2 coordinates)
  useEffect(() => {
    try {
      const { next, L, open } = applyQueryToState();
      if (next.searchType) setSearchType(next.searchType);
      setFilters((f) => ({
        ...f,
        radius: next.radius ?? f.radius,
        restaurant_type: next.cuisine ?? f.restaurant_type,
        price_level: next.price != null ? next.price : f.price_level,
        min_rating: next.min != null ? next.min : f.min_rating,
        open_now: open || f.open_now,
      }));
      if (L && L.length) setLocations(L);
    } catch {}
  }, []);

  // Load recent searches
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("miparcours_recent_searches") || "[]");
      setRecentSearches(Array.isArray(saved) ? saved : []);
    } catch {}
  }, []);

  // Persist state + push URL (debounced)
  useEffect(() => {
    let timer;
    const snapshot = { locations, searchType, filters, resultViewMode };
    try {
      const q = serializeStateToQuery({ searchType, filters, locations });
      const url = q ? `?${q}` : window.location.pathname;
      window.history.replaceState(null, "", url);
    } catch {}
    try {
      timer = setTimeout(() => {
        localStorage.setItem("miparcours_state_v1", JSON.stringify(snapshot));
      }, 400);
    } catch {}
    return () => clearTimeout(timer);
  }, [locations, searchType, filters, resultViewMode]);

  // Auto-search (debounced)
  useEffect(() => {
    if (!autoSearch) return;
    const t = setTimeout(() => {
      searchRestaurants();
    }, 600);
    return () => clearTimeout(t);
  }, [filters, searchType, locations, autoSearch]);

  // Prefetch details when cards appear in viewport
  useEffect(() => {
    if (!results?.restaurants?.length) return;
    const els = document.querySelectorAll("[data-observe-id]");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el, i) => {
        if (i < 3) prefetchPlaceDetails(el.getAttribute("data-observe-id"));
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const id = e.target.getAttribute("data-observe-id");
            if (id) prefetchPlaceDetails(id);
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "200px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [results]);

  /** ---------- Geolocation & Locations ---------- **/
  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation is not supported"));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err)
      );
    });

  const addLocation = () => setLocations((prev) => [...prev, { address: "", lat: null, lng: null }]);
  const removeLocation = (index) => {
    setLocations((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };
  const updateLocation = (index, field, value) => {
    const newLocations = [...locations];
    if (typeof value === "object" && value !== null) {
      newLocations[index] = { address: value.address || "", lat: value.lat, lng: value.lng };
    } else {
      newLocations[index][field] = value;
    }
    setLocations(newLocations);
  };
  const handleAddressSelect = (index, selectedAddress) => {
    try {
      const saved = JSON.parse(localStorage.getItem("miparcours_recent_addresses") || "[]");
      const next = [selectedAddress, ...saved.filter((s) => s.address !== selectedAddress.address)].slice(0, 10);
      localStorage.setItem("miparcours_recent_addresses", JSON.stringify(next));
    } catch {}
    updateLocation(index, null, selectedAddress);
  };
  const handleButtonPress = (cb) => {
    if (isMobile) vibrate(50);
    cb();
  };
  const openMapSelector = (index) => {
    setMapSelectorIndex(index);
    setShowMapSelector(true);
  };
  const handleMapLocationSelect = (selectedLocation) => {
    updateLocation(mapSelectorIndex, null, selectedLocation);
    setShowMapSelector(false);
  };

  /** ---------- Search (front-only via Places) ---------- **/
  const searchRestaurants = async () => {
    try {
      if (locations.length < 2) {
        setError("Veuillez ajouter au moins 2 localisations");
        return;
      }
      const validLocations = locations.filter(
        (loc) => (loc.address && loc.address.trim()) || (typeof loc.lat === "number" && typeof loc.lng === "number")
      );
      if (validLocations.length < 2) {
        setError("Veuillez fournir au moins 2 localisations valides");
        return;
      }

      setLoading(true);
      setError("");

      const { midpoint, places, total_count, meta } = await findPlacesAtMidpoint({
        locations: validLocations.map((l) => ({ lat: l.lat, lng: l.lng })),
        radius: filters.radius,
        searchType: searchType,
        restaurantType: filters.restaurant_type || null,
        minRating: filters.min_rating || null,
        priceLevel: filters.price_level ?? null,
        openNow: !!filters.open_now,
      });

      // Client-side sort preference
      const sorted = [...places].sort((a, b) => {
        if (sortOrder === "distance_asc") {
          const da = a.distance_from_midpoint ?? Infinity;
          const db = b.distance_from_midpoint ?? Infinity;
          return da - db;
        } else {
          const ra = a.rating ?? 0,
            rb = b.rating ?? 0;
          if (rb !== ra) return rb - ra;
          const da = a.distance_from_midpoint ?? Infinity;
          const db = b.distance_from_midpoint ?? Infinity;
          return da - db;
        }
      });

      setResults({ midpoint, restaurants: sorted, total_count, meta });

      try {
        const entry = {
          ts: Date.now(),
          type: searchType,
          radius: filters.radius,
          cuisine: filters.restaurant_type || null,
          price: filters.price_level ?? null,
          min: filters.min_rating ?? null,
          open: !!filters.open_now,
        };
        const saved = JSON.parse(localStorage.getItem("miparcours_recent_searches") || "[]");
        const next = [entry, ...saved].slice(0, 5);
        localStorage.setItem("miparcours_recent_searches", JSON.stringify(next));
        setRecentSearches(next);
      } catch {}
    } catch (e) {
      console.error("Erreur lors de la recherche (front):", e);
      setError("Erreur lors de la recherche (front). Vérifiez la clé Google Maps et le chargement de la librairie Places.");
    } finally {
      setLoading(false);
    }
  };

  /** ---------- Place details (front-only) ---------- **/
  const prefetchPlaceDetails = async (placeId) => {
    if (!placeId || prefetchCache[placeId]) return;
    try {
      if (!window.google?.maps?.places) return;
      const map = new window.google.maps.Map(document.createElement("div"));
      const service = new window.google.maps.places.PlacesService(map);
      const detail = await new Promise((resolve) => {
        service.getDetails(
          { placeId, fields: ["opening_hours", "website", "url", "formatted_phone_number", "rating"] },
          (d) => resolve(d || null)
        );
      });
      if (detail) setPrefetchCache((c) => ({ ...c, [placeId]: detail }));
    } catch {}
  };

  const getPlaceDetails = async (placeId) => {
    try {
      if (!window.google?.maps?.places) return;
      const map = new window.google.maps.Map(document.createElement("div"));
      const service = new window.google.maps.places.PlacesService(map);
      const detail = await new Promise((resolve) => {
        service.getDetails(
          {
            placeId,
            fields: [
              "name",
              "formatted_address",
              "formatted_phone_number",
              "website",
              "url",
              "rating",
              "opening_hours",
              "reviews",
              "photos",
              "geometry",
            ],
          },
          (d) => resolve(d || null)
        );
      });
      if (!detail) return;
      const photos =
        (detail.photos || [])
          .slice(0, 6)
          .map((p) => {
            const ref = typeof p.getUrl === "function" ? null : p.photo_reference;
            return typeof p.getUrl === "function" ? p.getUrl({ maxWidth: 800 }) : ref ? photoUrlFromReference(ref, 800) : null;
          })
          .filter(Boolean) || [];

      setPlaceDetails({
        name: detail.name,
        address: detail.formatted_address,
        phone: detail.formatted_phone_number || null,
        website: detail.website || null,
        rating: detail.rating || null,
        opening_hours: detail.opening_hours || null,
        reviews: detail.reviews || [],
        photos,
      });
      setSelectedPlace(placeId);
    } catch (error) {
      console.error("Erreur lors de l'obtention des détails (front):", error);
    }
  };

  const getPriceLevelDisplay = (level) => (level == null ? "N/A" : "€".repeat(Number(level)));
  const getRatingDisplay = (rating) =>
    !rating ? "N/A" : "★".repeat(Math.floor(rating)) + (rating % 1 >= 0.5 ? "☆" : "");

  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  /** ---------- UI ---------- **/
  return (
    <div className={isDark ? "min-h-screen bg-[#0b1220] text-gray-100" : "min-h-screen bg-gray-50"}>
      {/* Hero */}
      <div
        className="relative bg-cover bg-center h-96"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1528605248644-14dd04022da1?crop=entropy&cs=srgb&fm=jpg&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="text-center text-white">
            <h1 className="text-5xl font-bold mb-4">Trouvez le Lieu Parfait</h1>
            <p className="text-xl mb-8">Découvrez des restaurants et hôtels à mi-parcours entre vos amis</p>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          {recentSearches.length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-700">Dernières recherches</h3>
                <button
                  onClick={() => {
                    localStorage.removeItem("miparcours_recent_searches");
                    setRecentSearches([]);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Effacer
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSearchType(r.type);
                      setFilters({
                        radius: r.radius,
                        restaurant_type: r.cuisine || "",
                        price_level: r.price,
                        min_rating: r.min,
                        open_now: !!r.open,
                      });
                    }}
                    className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
                  >
                    {r.type} • {Math.round(r.radius / 1000)}km{r.open ? " • ouverts" : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Où vous retrouvez-vous ?</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    if (canShare) {
                      await navigator.share({ title: "MiParcours", url: window.location.href });
                    } else {
                      await navigator.clipboard.writeText(window.location.href);
                    }
                  } catch {}
                }}
                className="text-sm px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700"
              >
                Partager
              </button>
              <button
                onClick={() => setIsDark((d) => !d)}
                className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-500"
              >
                {isDark ? "Mode clair" : "Mode sombre"}
              </button>
            </div>
          </div>

          {/* Quick Chips */}
          <Chips
            autoSearch={autoSearch}
            setAutoSearch={setAutoSearch}
            filters={filters}
            setFilters={setFilters}
            setSortOrder={setSortOrder}
            onSearch={searchRestaurants}
          />

          {/* Search type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Type de recherche</label>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  id="searchType-restaurant"
                  type="radio"
                  name="searchType"
                  value="restaurant"
                  checked={searchType === "restaurant"}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="mr-2 h-5 w-5"
                />
                <label htmlFor="searchType-restaurant" className="select-none cursor-pointer py-2">
                  🍽️ Restaurants
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="searchType-hotel"
                  type="radio"
                  name="searchType"
                  value="hotel"
                  checked={searchType === "hotel"}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="mr-2 h-5 w-5"
                />
                <label htmlFor="searchType-hotel" className="select-none cursor-pointer py-2">
                  🏨 Hôtels
                </label>
              </div>
            </div>
          </div>

          {/* Locations */}
          <div className="space-y-4 mb-6">
            {locations.map((location, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Localisation {index + 1}
                  </label>
                  <div className="space-y-2">
                    <AddressAutocomplete
                      value={location.address}
                      onChange={(selectedAddress) => handleAddressSelect(index, selectedAddress)}
                      placeholder={`Tapez une adresse (ex: Tour Eiffel, Paris)`}
                      className="w-full"
                    />
                    <RecentAddresses onPick={(a) => updateLocation(index, null, a)} />
                    {location.lat != null && location.lng != null && (
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        ✅ Coordonnées obtenues: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => handleButtonPress(() => openMapSelector(index))}
                    className="mobile-touch-target px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition duration-200 text-sm"
                    title="Sélectionner sur la carte"
                  >
                    🗺️
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const loc = await getCurrentLocation();
                        const newLocations = [...locations];
                        newLocations[index] = {
                          address: `Ma position actuelle (${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)})`,
                          lat: loc.lat,
                          lng: loc.lng,
                        };
                        setLocations(newLocations);
                      } catch (e) {
                        console.error(e);
                        setError("Impossible d'obtenir votre position actuelle");
                      }
                    }}
                    className="mobile-touch-target px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 text-sm"
                    title="Utiliser ma position actuelle"
                  >
                    📍
                  </button>
                </div>
                {locations.length > 1 && (
                  <button
                    onClick={() => removeLocation(index)}
                    className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addLocation}
            className="mb-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
          >
            + Ajouter une localisation
          </button>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rayon : {Math.round(filters.radius / 1000)} km
              </label>
              <input
                type="range"
                min="500"
                max="20000"
                step="500"
                value={filters.radius}
                onChange={(e) => setFilters({ ...filters, radius: parseInt(e.target.value, 10) })}
                className="w-full"
              />
            </div>

            {searchType === "restaurant" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de cuisine</label>
                <input
                  value={filters.restaurant_type}
                  onChange={(e) => setFilters({ ...filters, restaurant_type: e.target.value })}
                  placeholder="Italien, sushi, bistro..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {searchType === "hotel" ? "Gamme d'hôtel" : "Gamme de prix"}
              </label>
              <select
                value={filters.price_level ?? ""}
                onChange={(e) =>
                  setFilters({ ...filters, price_level: e.target.value ? parseInt(e.target.value, 10) : null })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous prix</option>
                <option value={1}>€ (Économique)</option>
                <option value={2}>€€ (Modéré)</option>
                <option value={3}>€€€ (Cher)</option>
                <option value={4}>€€€€ (Très cher)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note minimum</label>
              <select
                value={filters.min_rating ?? ""}
                onChange={(e) =>
                  setFilters({ ...filters, min_rating: e.target.value ? parseFloat(e.target.value) : null })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes notes</option>
                <option value={3}>3+ étoiles</option>
                <option value={4}>4+ étoiles</option>
                <option value={4.5}>4.5+ étoiles</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="inline-flex items-center text-sm text-gray-700">
              <input
                id="openNow"
                type="checkbox"
                className="mr-2"
                checked={!!filters.open_now}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFilters({ ...filters, open_now: checked });
                  if (autoSearch) {
                    // Lancer immédiatement pour ressentir l'effet sur mobile
                    searchRestaurants();
                  }
                }}
              />
              <label htmlFor="openNow" className="select-none cursor-pointer py-2">Ouverts maintenant</label>
            </div>

            <div className="inline-flex items-center text-sm text-gray-700">
              <input
                id="autoSearch"
                type="checkbox"
                className="mr-2"
                checked={!!autoSearch}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setAutoSearch(enabled);
                  if (enabled) {
                    const valid = (locations || []).filter(
                      (loc) =>
                        (loc.address && loc.address.trim()) ||
                        (typeof loc.lat === "number" && typeof loc.lng === "number")
                    );
                    if (valid.length >= 2) {
                      // Recherche immédiate au toggle ON
                      searchRestaurants();
                    }
                  }
                }}
              />
              <label htmlFor="autoSearch" className="select-none cursor-pointer py-2">Rechercher automatiquement</label>
            </div>
          </div>

          <button
            onClick={() => handleButtonPress(searchRestaurants)}
            disabled={loading}
            className="btn-primary w-full px-6 py-3 bg-indigo-600 text-white rounded-lg disabled:bg-gray-400 transition duration-200 text-lg font-semibold mobile-touch-target"
          >
            {loading ? "Recherche en cours..." : `Trouver des ${searchType === "restaurant" ? "restaurants" : "hôtels"}`}
          </button>

          {autoHint && (
            <div className="mt-3 p-3 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">{autoHint}</div>
          )}
          {error && <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>}
        </div>

        {/* Results */}
        {(loading || results) && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-800">
                {searchType === "restaurant" ? "Restaurants" : "Hôtels"} {results ? `trouvés (${results.total_count})` : ""}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      if (canShare) {
                        await navigator.share({ title: "MiParcours", url: window.location.href });
                      } else {
                        await navigator.clipboard.writeText(window.location.href);
                      }
                    } catch {}
                  }}
                  className="text-sm px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700"
                >
                  Partager
                </button>
                <button
                  onClick={() => setIsDark((d) => !d)}
                  className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                >
                  {isDark ? "Mode clair" : "Mode sombre"}
                </button>
              </div>
            </div>

            {results && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Point de rencontre: {results.midpoint.address}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Résultats chargés : <strong>{results.restaurants.length}</strong>
                  {results.meta?.fetched ? (
                    <>
                      {" "}/ <span className="text-gray-600">{results.meta.fetched}</span>
                    </>
                  ) : null}
                </p>
              </div>
            )}

            <div className="mb-6">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setResultViewMode("list")}
                  className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
                    resultViewMode === "list" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  📋 Vue Liste
                </button>
                <button
                  onClick={() => setResultViewMode("map")}
                  className={`px-4 py-2 font-medium text-sm transition-colors duration-200 ${
                    resultViewMode === "map" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  🗺️ Vue Carte
                </button>
              </div>
            </div>

            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
            )}

            {!loading && results && (resultViewMode === "map" ? (
              <ResultsMapView results={results} onPlaceSelect={getPlaceDetails} searchType={searchType} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {results.restaurants.map((place) => (
                  <div
                    key={place.place_id}
                    data-observe-id={place.place_id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition duration-200"
                  >
                    {place.photo_url && (
                      <img
                        src={place.photo_url}
                        alt={place.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <h3 className="font-bold text-lg mb-2 text-gray-900">{place.name}</h3>
                        <button
                          title="Ajouter aux favoris"
                          className="text-xl"
                          onClick={() => {
                            try {
                              const favs = JSON.parse(localStorage.getItem("miparcours_favs") || "[]");
                              const next = favs.includes(place.place_id)
                                ? favs.filter((id) => id !== place.place_id)
                                : [place.place_id, ...favs];
                              localStorage.setItem("miparcours_favs", JSON.stringify(next));
                            } catch {}
                          }}
                        >
                          💙
                        </button>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{place.address}</p>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-yellow-500 flex items-center">
                          {getRatingDisplay(place.rating)}{" "}
                          {place.rating && <span className="ml-1 text-gray-700">{place.rating}</span>}
                        </span>
                        <span className="text-green-600 font-semibold">
                          {place.price_level != null ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border-emerald-200 border">
                              {getPriceLevelDisplay(place.price_level)}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </span>
                      </div>
                      {place.distance_from_midpoint != null && (
                        <p className="text-xs text-gray-500 mb-3">
                          Distance: {Math.round(place.distance_from_midpoint)}m du point médian
                        </p>
                      )}
                      {place.opening_hours?.open_now !== undefined && (
                        <span
                          className={`inline-block mb-3 px-2 py-1 rounded text-xs ${
                            place.opening_hours.open_now ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}
                        >
                          {place.opening_hours.open_now ? "Ouvert" : "Fermé"}
                        </span>
                      )}
                      <div className="action-buttons space-y-2">
                        <div className="grid grid-cols-1 gap-2">
                          {place.phone_number && (
                            <a
                              href={`tel:${place.phone_number}`}
                              onClick={() => {
                                isMobile && vibrate(30);
                              }}
                              className="mobile-touch-target flex items-center justify-center px-3 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition duration-200"
                            >
                              📞 Appeler
                            </a>
                          )}
                          {place.reservation_url && (
                            <a
                              href={place.reservation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => isMobile && vibrate(30)}
                              className="mobile-touch-target flex items-center justify-center px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition duration-200"
                            >
                              {searchType === "restaurant" ? "🍽️ Réserver" : "🏨 Réserver"}
                            </a>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            {place.website && (
                              <a
                                href={place.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  isMobile && vibrate(20);
                                }}
                                className="mobile-touch-target flex items-center justify-center px-2 py-2 bg-gray-500 text-white text-xs rounded-lg hover:bg-gray-600 transition duration-200"
                              >
                                🌐 Site
                              </a>
                            )}
                            {place.google_maps_url && (
                              <a
                                href={place.google_maps_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  isMobile && vibrate(20);
                                }}
                                className="mobile-touch-target flex items-center justify-center px-2 py-2 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition duration-200"
                              >
                                📍 Carte
                              </a>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleButtonPress(() => getPlaceDetails(place.place_id))}
                          className="mobile-touch-target w-full px-3 py-2 bg-indigo-500 text-white text-sm rounded-lg hover:bg-indigo-600 transition duration-200"
                        >
                          ℹ️ Plus d'infos
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Bottom Sheet (mobile) or Modal (desktop) for details */}
        {selectedPlace && placeDetails && (
          <>
            {/* Desktop modal */}
            <div className="hidden md:flex fixed inset-0 bg-black bg-opacity-50 items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold">{placeDetails.name}</h3>
                    <button
                      onClick={() => {
                        setSelectedPlace(null);
                        setPlaceDetails(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                  {placeDetails.photos && placeDetails.photos.length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-2 gap-2">
                        {placeDetails.photos.slice(0, 4).map((photo, index) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`${placeDetails.name} ${index + 1}`}
                            className="w-full h-32 object-cover rounded"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <PlaceDetailFields placeDetails={placeDetails} />
                </div>
              </div>
            </div>

            {/* Mobile bottom sheet */}
            <div className="md:hidden fixed inset-x-0 bottom-0 z-50">
              <div className="bg-black/50 absolute inset-0" onClick={() => { setSelectedPlace(null); setPlaceDetails(null); }} />
              <div className="relative mx-auto max-w-screen-sm bg-white rounded-t-2xl shadow-xl">
                <div className="w-10 h-1.5 bg-gray-300 rounded mx-auto my-3" />
                <div className="px-4 pb-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold">{placeDetails.name}</h3>
                    <button
                      onClick={() => {
                        setSelectedPlace(null);
                        setPlaceDetails(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 text-2xl -mt-2"
                    >
                      ×
                    </button>
                  </div>
                  {placeDetails.photos && placeDetails.photos.length > 0 && (
                    <div className="mb-3">
                      <div className="grid grid-cols-2 gap-2">
                        {placeDetails.photos.slice(0, 4).map((photo, index) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`${placeDetails.name} ${index + 1}`}
                            className="w-full h-24 object-cover rounded"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <PlaceDetailFields placeDetails={placeDetails} />
                </div>
              </div>
            </div>
          </>
        )}

        <MapAddressSelector
          isOpen={showMapSelector}
          onClose={() => setShowMapSelector(false)}
          onLocationSelect={handleMapLocationSelect}
          initialCenter={
            locations.length > 0 && locations[0].lat && locations[0].lng
              ? { lat: locations[0].lat, lng: locations[0].lng }
              : { lat: 48.8566, lng: 2.3522 }
          }
        />

        <InstallPrompt />

        {showTour && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className={isDark ? "bg-[#111827] text-white" : "bg-white"} style={{ maxWidth: 560 }}>
              <div className="rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold mb-3">Bienvenue sur Mi-Parcours 👋</h3>
                <ol className="list-decimal pl-5 space-y-2 text-sm">
                  <li>Entrez <strong>deux adresses</strong> (ou utilisez 📍 pour votre position).</li>
                  <li>Ajustez le <strong>rayon</strong>, le <strong>type</strong>, la <strong>note</strong>, et cochez <em>Ouverts maintenant</em> si besoin.</li>
                  <li>Cliquez sur <strong>Rechercher</strong> (ou activez <em>Rechercher automatiquement</em>).</li>
                  <li>Partagez le résultat avec le bouton <em>Partager</em>.</li>
                </ol>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowTour(false);
                      try {
                        localStorage.setItem("miparcours_tour_done", "1");
                      } catch {}
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    J’ai compris
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile fixed action bar */}
      <FixedActionBar
        searchRestaurants={searchRestaurants}
        autoSearch={autoSearch}
        setAutoSearch={setAutoSearch}
        filters={filters}
        setFilters={setFilters}
      />
    </div>
  );
}

function PlaceDetailFields({ placeDetails }) {
  return (
    <>
      <div className="space-y-3">
        <p>
          <strong>Adresse:</strong> {placeDetails.address}
        </p>
        {placeDetails.phone && <p><strong>Téléphone:</strong> {placeDetails.phone}</p>}
        {placeDetails.website && (
          <p>
            <strong>Site web:</strong>{" "}
            <a href={placeDetails.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
              {placeDetails.website}
            </a>
          </p>
        )}
        {placeDetails.rating && <p><strong>Note:</strong> {placeDetails.rating}</p>}
      </div>
      {placeDetails.opening_hours?.weekday_text && (
        <div className="mt-4">
          <h4 className="font-semibold mb-2">Horaires d'ouverture:</h4>
          <ul className="text-sm space-y-1">
            {placeDetails.opening_hours.weekday_text.map((hours, index) => (
              <li key={index}>{hours}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** ---------- Recent addresses ---------- **/
function RecentAddresses({ onPick }) {
  const [items, setItems] = React.useState([]);
  useEffect(() => {
    try {
      const arr = JSON.parse(localStorage.getItem("miparcours_recent_addresses") || "[]");
      setItems(Array.isArray(arr) ? arr : []);
    } catch {}
  }, []);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {items.slice(0, 6).map((it, idx) => (
        <button
          key={idx}
          onClick={() => onPick(it)}
          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border"
        >
          {it.address || `${it.lat?.toFixed(4)},${it.lng?.toFixed(4)}`}
        </button>
      ))}
    </div>
  );
}

export default App;
